<template>
  <div class="extended-trial-balance-report">
    <!-- Control Buttons — same as ExtendedTrialBalancePrint.vue -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0 mr-2">Extended Trial Balance</h4>
        <b-badge v-show="!loading" variant="light-info">
          {{ filteredItems.length }} Accounts
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">
        Calculating balances and generating report...
      </p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <!-- Header Section — same as ExtendedTrialBalancePrint.vue -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Extended Trial Balance Report</span>
              <span class="date-range-text ml-2">
                <strong>{{ formatDate(dtFrom) }}</strong> to
                <strong>{{ formatDate(dtTo) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ABA layout: account line + DR / CR (closing balance) -->
      <table class="aba-etb-table">
        <thead>
          <tr class="aba-head-row">
            <th class="aba-col-account" />
            <th class="aba-col-amt aba-head-dr text-right">DR</th>
            <th class="aba-col-amt aba-head-cr text-right">CR</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, rIdx) in abaVisualRows">
            <tr
              v-if="row.t === 'nature'"
              :key="'n-' + row.nature + '-' + rIdx"
              class="aba-nature-row"
            >
              <td colspan="3" class="aba-nature-label">{{ row.nature }}</td>
            </tr>

            <tr
              v-else-if="row.t === 'account'"
              :key="'a-' + rIdx"
              :class="getAccountRowClass(row.item)"
            >
              <td
                class="aba-account-cell"
                :style="{ paddingLeft: accountIndentPx(row.item) + 'px' }"
              >
                <a
                  href="#"
                  class="aba-account-link"
                  @click.prevent="openLedger(row.item)"
                >
                  <span v-if="row.item.accountCode" class="aba-account-code">{{
                    row.item.accountCode
                  }}</span>
                  <span class="aba-account-title">{{
                    row.item.accountTitle
                  }}</span>
                </a>
              </td>
              <td class="aba-amt-cell text-right">
                {{ formatAccountDrCr(row.item, "dr") }}
              </td>
              <td class="aba-amt-cell text-right">
                {{ formatAccountDrCr(row.item, "cr") }}
              </td>
            </tr>

            <tr
              v-else-if="row.t === 'subtotal'"
              :key="'s-' + rIdx"
              class="aba-subtotal-row"
            >
              <td
                class="aba-subtotal-label"
                :style="{ paddingLeft: accountIndentPx(row.parentItem) + 'px' }"
              >
                {{ subtotalLabel(row.parentTitle) }}
              </td>
              <td class="aba-amt-cell text-right">
                <span class="aba-total-box">{{
                  formatDrCrAmount(row.dr, "dr")
                }}</span>
              </td>
              <td class="aba-amt-cell text-right">
                <span class="aba-total-box">{{
                  formatDrCrAmount(row.cr, "cr")
                }}</span>
              </td>
            </tr>

            <tr
              v-else-if="row.t === 'section-end'"
              :key="'e-' + rIdx"
              class="aba-section-gap"
            >
              <td colspan="3" />
            </tr>
          </template>

          <tr class="aba-grand-row">
            <td class="aba-grand-label text-right">GRAND TOTAL</td>
            <td class="aba-amt-cell text-right">
              <span class="aba-total-box aba-grand-box">{{
                formatDrCrAmount(grandClosingDr, "dr")
              }}</span>
            </td>
            <td class="aba-amt-cell text-right">
              <span class="aba-total-box aba-grand-box">{{
                formatDrCrAmount(grandClosingCr, "cr")
              }}</span>
            </td>
          </tr>

          <tr class="aba-diff-row">
            <td class="aba-grand-label text-right">DEBIT - CREDIT DIFFERENCE</td>
            <td class="aba-amt-cell text-right">
              <span v-if="grandDifferenceDr > 0" class="aba-total-box aba-grand-box">{{
                formatCurrency(grandDifferenceDr)
              }}</span>
              <span v-else>-</span>
            </td>
            <td class="aba-amt-cell text-right">
              <span v-if="grandDifferenceCr > 0" class="aba-total-box aba-grand-box">{{
                formatCurrency(grandDifferenceCr)
              }}</span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="page-footer">
        <small>Powered by MySkool.app | Generated On: {{ currentDateTime }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

/**
 * ABA (myskoolaba) extended trial balance — same APIs as ExtendedTrialBalancePrint.vue;
 * table layout matches client sample (code + name / DR / CR with group totals).
 */
export default {
  name: "AbaExtrendedtrailbalcne",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
    };
  },
  computed: {
    filteredItems() {
      return this.reportData || [];
    },
    groupedData() {
      if (!this.reportData || !Array.isArray(this.reportData)) return {};

      const groups = {};
      const natureTemplate = [
        "ASSET",
        "EXPENSE",
        "LIABILITY",
        "EQUITY",
        "REVENUE",
      ];

      this.reportData.forEach((item) => {
        let nature = (item.nature || "OTHER").toUpperCase();
        if (nature === "ASSETS") nature = "ASSETS";
        if (nature === "LIABILITIES") nature = "LIABILITIES";
        if (nature === "EXPENSES") nature = "EXPENSE";
        if (nature === "REVENUES") nature = "REVENUE";
        if (nature === "EQUITIES") nature = "EQUITY";

        if (!groups[nature]) groups[nature] = [];
        groups[nature].push(item);
      });

      Object.keys(groups).forEach((nature) => {
        groups[nature].sort((a, b) =>
          (a.accountCode || "").localeCompare(b.accountCode || "", undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      });

      const sortedGroups = {};
      natureTemplate.forEach((n) => {
        if (groups[n]) sortedGroups[n] = groups[n];
      });
      Object.keys(groups).forEach((key) => {
        if (!natureTemplate.includes(key)) sortedGroups[key] = groups[key];
      });

      return sortedGroups;
    },
    grandClosingDr() {
      return this.sumLeafClosingSide("dr");
    },
    grandClosingCr() {
      return this.sumLeafClosingSide("cr");
    },
    grandDifferenceDr() {
      const diff = this.grandClosingDr - this.grandClosingCr;
      return diff > 0 ? diff : 0;
    },
    grandDifferenceCr() {
      const diff = this.grandClosingCr - this.grandClosingDr;
      return diff > 0 ? diff : 0;
    },
    abaVisualRows() {
      const out = [];
      const grouped = this.groupedData;
      const keys = Object.keys(grouped);

      keys.forEach((nature) => {
        const flat = grouped[nature];
        if (!flat || !flat.length) return;

        out.push({ t: "nature", nature });
        const withTotals = this.buildRowsWithParentTotals(flat);
        withTotals.forEach((r) => out.push(r));
        out.push({ t: "section-end" });
      });

      return out;
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.dtFrom = query.dtFrom;
      this.dtTo = query.dtTo;

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db || !this.cID) {
        this.error = "Missing required parameters (db, campusID).";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data.length > 0
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) baseUrl += "/";

        const url = `${baseUrl}TrialBalanceData/ExtendedTrialBalance`;

        const response = await axios.get(url, {
          params: {
            _db: this.db,
            campusID: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (Array.isArray(response.data)) {
          this.reportData = response.data;
        } else if (response.data && response.data.data) {
          this.reportData = response.data.data;
        } else {
          this.error = "Invalid data format received from server.";
        }
      } catch (error) {
        console.error("Failed to load Extended TB (ABA):", error);
        this.error = "Network error or server unavailable.";
      } finally {
        this.loading = false;
      }
    },
    buildRowsWithParentTotals(flat) {
      const out = [];
      let i = 0;

      const sumLeavesBetween = (from, until) => {
        let dr = 0;
        let cr = 0;
        for (let j = from; j < until; j++) {
          if (!flat[j].isParent) {
            const amounts = this.getClosingNetAmounts(flat[j]);
            dr += amounts.dr;
            cr += amounts.cr;
          }
        }
        return { dr, cr };
      };

      const walk = (stopLevel) => {
        while (i < flat.length && flat[i].level > stopLevel) {
          const item = flat[i];
          const childFrom = i + 1;
          i += 1;
          out.push({ t: "account", item });

          if (!item.isParent) continue;

          walk(item.level);
          const sums = sumLeavesBetween(childFrom, i);
          out.push({
            t: "subtotal",
            parentTitle: item.accountTitle,
            parentItem: item,
            dr: sums.dr,
            cr: sums.cr,
          });
        }
      };

      walk(0);
      return out;
    },
    sumLeafClosingSide(side) {
      if (!this.reportData || !Array.isArray(this.reportData)) return 0;
      return this.reportData.reduce((sum, item) => {
        if (item.isParent) return sum;
        const amounts = this.getClosingNetAmounts(item);
        return sum + (amounts[side] || 0);
      }, 0);
    },
    getClosingSignedNet(item) {
      const debit = Number(item?.closingDebit);
      const credit = Number(item?.closingCredit);
      const debitVal = Number.isFinite(debit) ? debit : 0;
      const creditVal = Number.isFinite(credit) ? credit : 0;

      if (debitVal !== 0 || creditVal !== 0) {
        if (debitVal !== 0 && creditVal === 0) return debitVal;
        if (creditVal !== 0 && debitVal === 0) return -creditVal;
        return debitVal - creditVal;
      }

      if (
        item &&
        item.closingNet !== undefined &&
        item.closingNet !== null &&
        item.closingNet !== ""
      ) {
        return Number(item.closingNet) || 0;
      }

      return 0;
    },
    splitClosingNet(net) {
      const value = Number(net) || 0;
      return {
        dr: value > 0 ? value : 0,
        cr: value < 0 ? Math.abs(value) : 0,
      };
    },
    getClosingNetAmounts(item) {
      return this.splitClosingNet(this.getClosingSignedNet(item));
    },
    accountIndentPx(item) {
      const lv = Number(item?.level) || 1;
      return 4 + Math.max(0, lv - 1) * 22;
    },
    getAccountRowClass(item) {
      const lv = Number(item.level) || 1;
      return {
        "aba-row-l1": lv === 1,
        "aba-row-l2": lv === 2,
        "aba-row-l3": lv === 3,
        "aba-row-leaf": !item.isParent,
        "aba-row-parent": item.isParent,
      };
    },
    subtotalLabel(title) {
      return `Total - - - - ${title || ""}`;
    },
    formatDrCrCell(val, side) {
      const n = Number(val) || 0;
      if (side === "dr" && n > 0) return this.formatCurrency(n);
      if (side === "cr" && n > 0) return this.formatCurrencyInBrackets(n);
      return "-";
    },
    formatAccountDrCr(item, side) {
      if (item.isParent) return "-";
      const amounts = this.getClosingNetAmounts(item);
      const val = side === "dr" ? amounts.dr : amounts.cr;
      return this.formatDrCrCell(val, side);
    },
    formatDrCrAmount(val, side) {
      const n = Number(val) || 0;
      if (side === "dr" && n > 0) return this.formatCurrency(n);
      if (side === "cr" && n > 0) return this.formatCurrencyInBrackets(n);
      return "-";
    },
    formatCurrencyInBrackets(value) {
      const formatted = this.formatCurrency(value);
      if (formatted === "-") return "-";
      return `(${formatted})`;
    },
    formatCurrency(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n === 0) return "-";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(n));
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    openLedger(item) {
      const routeData = this.$router.resolve({
        name: "advance-ledger-print",
        query: {
          db: this.db,
          cID: this.cID,
          accountID: item.accountID,
          dtFrom: this.dtFrom,
          dtTo: this.dtTo,
        },
      });
      window.open(routeData.href, "_blank");
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
/* —— Shared shell (matches ExtendedTrialBalancePrint.vue) —— */
.extended-trial-balance-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 4rem 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1750px;
  margin: 30px auto;
  transition: all 0.3s ease;
  overflow: hidden;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.page-footer {
  margin-top: 2rem;
  text-align: center;
  color: #636e72;
}

/* —— ABA table (client sample: no grid, DR/CR right, boxed group totals) —— */
.aba-etb-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.92rem;
  margin-top: 1.5rem;
  color: #000;

  th,
  td {
    border: none;
    padding: 5px 8px;
    vertical-align: middle;
    background: #fff;
  }
}

.aba-col-account {
  width: auto;
}

.aba-col-amt {
  width: 130px;
}

.aba-head-row th {
  padding-bottom: 6px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #000;
}

.aba-head-dr,
.aba-head-cr {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.aba-nature-row {
  .aba-nature-label {
    font-weight: 800;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 18px;
    padding-bottom: 6px;
    color: #000;
  }
}

.aba-account-link {
  color: #000;
  text-decoration: none;
  display: inline;
  line-height: 1.45;

  &:hover {
    color: #2e86de;
    .aba-account-code,
    .aba-account-title {
      text-decoration: underline;
    }
  }
}

.aba-account-code,
.aba-account-title {
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
}

.aba-account-code {
  margin-right: 0.45rem;
  white-space: nowrap;
}

.aba-account-title {
  word-wrap: break-word;
}

.aba-row-l1,
.aba-row-l2,
.aba-row-l3,
.aba-row-parent {
  .aba-account-link {
    font-weight: 700;
  }
}

.aba-row-leaf .aba-account-link {
  font-weight: 400;
}

.aba-amt-cell {
  font-family: "Outfit", sans-serif;
  font-size: 0.9rem;
  white-space: nowrap;
  color: #000;
}

.aba-subtotal-row {
  td {
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .aba-subtotal-label {
    font-weight: 700;
    font-size: 0.92rem;
  }
}

.aba-total-box {
  display: inline-block;
  min-width: 108px;
  padding: 2px 8px;
  border: 1px solid #000;
  font-weight: 700;
  text-align: right;
  line-height: 1.35;
}

.aba-section-gap td {
  height: 14px;
  padding: 0;
  border-bottom: 3px solid #000;
}

.aba-grand-row {
  td {
    padding-top: 14px;
    padding-bottom: 6px;
  }

  .aba-grand-label {
    font-weight: 800;
    font-size: 1rem;
    padding-right: 12px;
  }
}

.aba-grand-box {
  border-width: 2px;
}

.aba-diff-row {
  td {
    padding-top: 10px;
    padding-bottom: 6px;
  }

  .aba-grand-label {
    font-weight: 700;
    font-size: 0.95rem;
    padding-right: 12px;
  }
}

@page {
  size: portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .extended-trial-balance-report {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1cm 1.5cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-header-modern {
    margin-bottom: 5px !important;
    padding-bottom: 5px !important;

    .school-logo-img {
      height: 60px !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
    }

    .report-info-line {
      font-size: 0.9rem !important;
    }
  }

  .aba-etb-table {
    font-size: 9.5pt !important;

    th,
    td {
      padding: 3px 6px !important;
    }
  }

  .aba-total-box {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .page-footer {
    display: none;
  }
}
</style>
